/**
 * Queue-Dashboard — Werkstatt-Visibility in den OCR-Queue + Worker-Status.
 *
 * Polled-View auf GET /api/queue/status?queue=card.ocr.
 *
 * Macht für den Owner ohne Agent-Frage sichtbar:
 *   - WAS gerade aktiv ist (welcher Job, welche Karte, wie lange schon)
 *   - WORAUF gewartet wird (Position in Queue, ETA)
 *   - OB der Worker lebt (recency des letzten completed Jobs)
 *   - WANN zuletzt etwas fehlgeschlagen ist
 *
 * Die eigene Karte wird hervorgehoben.
 */
import { useEffect, useState } from "react";

interface QueueJob {
  id: string;
  name: string;
  state: "active" | "retry" | "created";
  retry_count: number;
  created_on: string;
  started_on: string | null;
  age_s: number;
  runtime_s: number | null;
  card_id: string | null;
}

interface QueueStatus {
  queue: string;
  jobs: QueueJob[];
  summary: {
    active: number;
    retry: number;
    created: number;
    total: number;
    avg_runtime_s: number | null;
    avg_runtime_sample: number;
  };
  health: {
    worker_alive: boolean;
    last_completed_run_id: string | null;
    last_completed_card_id: string | null;
    last_completed_at: string | null;
    last_completed_age_s: number | null;
    last_failed_run_id: string | null;
    last_failed_card_id: string | null;
    last_failed_at: string | null;
    last_failed_output: unknown;
  };
  now: string;
}

export function QueueDashboard(props: { highlightCardId?: string; pollMs?: number }) {
  const { highlightCardId, pollMs = 4000 } = props;
  const [status, setStatus] = useState<QueueStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [, tick] = useState(0);

  useEffect(() => {
    let alive = true;
    const fetchStatus = () => {
      fetch("api/queue/status?queue=card.ocr", { credentials: "same-origin" })
        .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
        .then(j => { if (alive) { setStatus(j); setErr(null); } })
        .catch(e => { if (alive) setErr(e.message); });
    };
    fetchStatus();
    const t = setInterval(fetchStatus, pollMs);
    return () => { alive = false; clearInterval(t); };
  }, [pollMs]);

  // Tick for live duration counter
  useEffect(() => {
    const t = setInterval(() => tick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  if (err) {
    return (
      <div style={{ padding: 10, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, marginBottom: 14 }}>
        <strong>Queue-Status nicht erreichbar:</strong> {err}
      </div>
    );
  }
  if (!status) {
    return (
      <div style={{ padding: 10, background: "#f3f4f6", borderRadius: 6, marginBottom: 14, fontSize: 13, color: "#6b7280" }}>
        Queue-Status lädt…
      </div>
    );
  }

  const { jobs, summary, health } = status;
  const myJob = highlightCardId
    ? jobs.find(j => j.card_id === highlightCardId)
    : null;

  return (
    <div className="queue-dashboard" style={{
      marginBottom: 14,
      border: "1px solid #c7d2fe",
      borderRadius: 6,
      background: "#eef2ff",
    }}>
      {/* Header */}
      <div style={{
        padding: "8px 12px",
        borderBottom: "1px solid #c7d2fe",
        background: "#e0e7ff",
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        fontSize: 13,
      }}>
        <strong>Queue-Dashboard · card.ocr</strong>
        <span><strong>{summary.active}</strong> active</span>
        <span><strong>{summary.created}</strong> wartend</span>
        {summary.retry > 0 && <span style={{ color: "#dc2626" }}><strong>{summary.retry}</strong> retry</span>}
        {summary.avg_runtime_s != null && (
          <span style={{ color: "#6b7280" }}>
            ⌀ Run-Time: {formatDur(summary.avg_runtime_s * 1000)} (über {summary.avg_runtime_sample} Jobs)
          </span>
        )}
        <span style={{ marginLeft: "auto" }}>
          {health.worker_alive ? (
            <span style={{ color: "#10b981", fontWeight: 600 }}>● Worker alive</span>
          ) : (
            <span style={{ color: "#dc2626", fontWeight: 600 }}>● Worker schläft</span>
          )}
        </span>
      </div>

      {/* Worker liveness detail — jeder Frame-Anker mit Run-UUID + Card-UUID
          kopierbar, damit Owner ohne Agent-Frage nach "schau dir UUID X an"
          sofort den richtigen Kontext geben kann. (2026-05-21) */}
      <div style={{ padding: "8px 12px", fontSize: 12, color: "#374151", borderBottom: "1px solid #c7d2fe", display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
        {health.last_completed_at ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <strong>Letzter completed Job</strong>
            {health.last_completed_run_id && <RunIdInline runId={health.last_completed_run_id} label="Run" />}
            {health.last_completed_card_id && <CardIdInline cardId={health.last_completed_card_id} />}
            <span>· {new Date(health.last_completed_at).toLocaleString("de-AT")}</span>
            <span>· vor {formatDur((health.last_completed_age_s ?? 0) * 1000)}</span>
          </span>
        ) : (
          <em>Noch kein Job completed in dieser Run-Historie.</em>
        )}
        {health.last_failed_run_id && (
          <span style={{ color: "#dc2626", display: "inline-flex", alignItems: "center", gap: 6 }}>
            ⚠ <strong>Letzter Fail</strong>
            <RunIdInline runId={health.last_failed_run_id} label="Run" tone="danger" />
            {health.last_failed_card_id && <CardIdInline cardId={health.last_failed_card_id} tone="danger" />}
            <span>({new Date(health.last_failed_at!).toLocaleString("de-AT")})</span>
          </span>
        )}
      </div>

      {/* Jobs list */}
      {jobs.length === 0 ? (
        <div style={{ padding: 12, color: "#6b7280", fontStyle: "italic", fontSize: 13 }}>
          Keine aktiven/wartenden Jobs in der Queue.
        </div>
      ) : (
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#e0e7ff", textAlign: "left" }}>
              <th style={{ padding: "5px 10px" }}>State</th>
              <th style={{ padding: "5px 10px" }}>Run-UUID</th>
              <th style={{ padding: "5px 10px" }}>Karte</th>
              <th style={{ padding: "5px 10px", textAlign: "right" }}>Wartezeit</th>
              <th style={{ padding: "5px 10px", textAlign: "right" }}>Laufzeit</th>
              <th style={{ padding: "5px 10px", textAlign: "right" }}>ETA</th>
              <th style={{ padding: "5px 10px", textAlign: "right" }}>Retry</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j, idx) => {
              const isMine = highlightCardId && j.card_id === highlightCardId;
              // ETA for created jobs: (jobs_ahead × avg_runtime) + remaining of active
              const created = jobs.filter(x => x.state === "created");
              const active = jobs.find(x => x.state === "active");
              let etaSec: number | null = null;
              if (j.state === "created" && summary.avg_runtime_s) {
                const myIdx = created.findIndex(x => x.id === j.id);
                const remainingActive = active && active.runtime_s != null
                  ? Math.max(0, summary.avg_runtime_s - active.runtime_s)
                  : 0;
                etaSec = remainingActive + myIdx * summary.avg_runtime_s;
              }
              return (
                <tr key={j.id}
                    style={{
                      borderTop: "1px solid #c7d2fe",
                      background: isMine ? "#fef3c7" : "transparent",
                      fontWeight: isMine ? 600 : 400,
                    }}>
                  <td style={{ padding: "5px 10px" }}>
                    <span style={{
                      padding: "1px 8px", borderRadius: 10, fontSize: 10,
                      background: j.state === "active" ? "#3b82f6"
                                : j.state === "retry" ? "#dc2626"
                                : "#f59e0b",
                      color: "white", fontWeight: 600,
                    }}>
                      {j.state === "created" ? "wartend" : j.state}
                    </span>
                    {isMine && <span style={{ marginLeft: 6, fontSize: 11, color: "#92400e" }}>← deine</span>}
                  </td>
                  <td style={{ padding: "5px 10px", fontFamily: "monospace" }}>
                    <RunIdInline runId={j.id} />
                  </td>
                  <td style={{ padding: "5px 10px", fontFamily: "monospace", fontSize: 11 }}>
                    {j.card_id ? <CardIdInline cardId={j.card_id} /> : "—"}
                  </td>
                  <td style={{ padding: "5px 10px", textAlign: "right" }}>{formatDur(j.age_s * 1000)}</td>
                  <td style={{ padding: "5px 10px", textAlign: "right" }}>
                    {j.runtime_s != null ? formatDur(j.runtime_s * 1000) : "—"}
                  </td>
                  <td style={{ padding: "5px 10px", textAlign: "right", color: "#6b7280" }}>
                    {etaSec != null ? `~${formatDur(etaSec * 1000)}` : (j.state === "active" ? "läuft" : "—")}
                  </td>
                  <td style={{ padding: "5px 10px", textAlign: "right" }}>
                    {j.retry_count > 0 ? <span style={{ color: "#dc2626" }}>{j.retry_count}</span> : 0}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* My job summary if highlighted */}
      {highlightCardId && (
        <div style={{ padding: "8px 12px", borderTop: "1px solid #c7d2fe", fontSize: 12, color: "#374151" }}>
          {myJob ? (
            <>
              <strong>Deine Karte ist {myJob.state === "created" ? "in der Queue" : "aktiv"}.</strong>
              {myJob.state === "created" && summary.avg_runtime_s && (
                <>
                  {" "}Warten ~{formatDur((computeEta(myJob, jobs, summary.avg_runtime_s)) * 1000)}
                  {" — basierend auf "}{summary.avg_runtime_sample}{" jüngsten Completions."}
                </>
              )}
            </>
          ) : (
            <em>Deine Karte hat keinen aktiven Queue-Eintrag (= Job bereits durch oder noch nicht enqueued).</em>
          )}
        </div>
      )}
    </div>
  );
}

function RunIdInline({ runId, label, tone }: { runId: string; label?: string; tone?: "default" | "danger" }) {
  const palette = tone === "danger"
    ? { bg: "#fee2e2", fg: "#991b1b" }
    : { bg: "#fef3c7", fg: "#92400e" };
  return <UuidChip uuid={runId} prefix={label} bg={palette.bg} fg={palette.fg} />;
}

function CardIdInline({ cardId, tone }: { cardId: string; tone?: "default" | "danger" }) {
  // Card-UUID separat anzeigen → Owner kann direkt /timesheets/<card_id> öffnen
  // und/oder dem Agent sagen "schau dir Karte X an"
  const palette = tone === "danger"
    ? { bg: "#fef2f2", fg: "#7f1d1d" }
    : { bg: "#e0e7ff", fg: "#3730a3" };
  return <UuidChip uuid={cardId} prefix="Karte" bg={palette.bg} fg={palette.fg} />;
}

function UuidChip({ uuid, prefix, bg, fg }: { uuid: string; prefix?: string; bg: string; fg: string }) {
  const [copied, setCopied] = useState(false);
  function copy(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (navigator.clipboard?.writeText) void navigator.clipboard.writeText(uuid).catch(() => {});
      else {
        const ta = document.createElement("textarea");
        ta.value = uuid; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
    } catch { /* */ }
    try { window.getSelection()?.removeAllRanges(); } catch { /* fine */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 900);
  }
  return (
    <code
      onClick={copy}
      title={`Klick zum Kopieren — ${prefix ? prefix + " " : ""}${uuid}`}
      style={{
        cursor: "pointer", padding: "1px 6px", borderRadius: 3,
        background: copied ? "#10b981" : bg,
        color: copied ? "white" : fg,
        fontSize: 11, fontWeight: 600,
        userSelect: "none",
      }}
    >
      {copied ? "✓ kopiert" : `⧉ ${prefix ? prefix + " " : ""}${uuid.slice(0, 8)}`}
    </code>
  );
}

function computeEta(job: QueueJob, jobs: QueueJob[], avgSec: number): number {
  const created = jobs.filter(j => j.state === "created");
  const active = jobs.find(j => j.state === "active");
  const myIdx = created.findIndex(j => j.id === job.id);
  const remainingActive = active && active.runtime_s != null
    ? Math.max(0, avgSec - active.runtime_s)
    : 0;
  return remainingActive + myIdx * avgSec;
}

function formatDur(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return `${m}m ${rs.toString().padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h ${rm}m`;
}
